import type { AnalysisResult } from '../lib/types';

export const mockEarningsCall: AnalysisResult = {
  slug: 'sample-earnings-call',
  title: 'Apple Q2 FY26 Earnings Call',
  date: '2026-04-30T22:00:00Z',
  documentType: 'earnings-call',
  verdict: 'BULLISH',
  score: 0.7,
  confidence: 86,
  summary:
    'Confident tone, raising guidance, services strength offsets China softness — typical of a beat-and-raise quarter.',
  briefing: {
    overall:
      'Management struck a confident, declarative tone throughout the prepared remarks and Q&A. Revenue and EPS beat consensus; iPhone and services both grew double digits ex-FX. Forward guidance was raised. The single soft spot was Greater China, which management characterised as a timing issue rather than a structural concern.',
    dimensionBreakdown: [
      {
        label: 'Tone & Confidence',
        copy: 'High certainty language ("we expect", "we are confident") clusters in the guidance and capital-return blocks. No qualifiers stacking on revenue commentary.',
      },
      {
        label: 'Forward-Looking Posture',
        copy: 'Raise on revenue, services margin and gross margin ranges. Capex commentary unchanged. Buyback authorisation increased.',
      },
      {
        label: 'Hedging & Qualifiers',
        copy: 'Hedging concentrated around China and FX — both bracketed clearly with quantification, which reads as transparency rather than evasion.',
      },
      {
        label: 'Q&A Discipline',
        copy: 'Three non-answers across 14 analyst questions, all on competitive positioning vs. on-device AI. Direct answers on margins, capital allocation and supply.',
      },
      {
        label: 'Quantification',
        copy: '41 quantitative claims; 38 framed positively or neutrally, 3 negatively (China). All 41 align with their qualitative framing.',
      },
    ],
    entityLandscape:
      'Services and Vision Pro pulled positive sentiment; Greater China and the antitrust environment pulled negative. Notably positive: Apple Intelligence rollout cadence and iPhone 17 channel inventory.',
    trajectory:
      'Sentiment opens neutral on the safe-harbour, climbs through the prepared remarks (peak in the services and capital-return sections), dips slightly in the China block, recovers and holds through Q&A.',
    linguisticProfile:
      'Active voice dominant. Below-average hedge density vs. the prior four quarters. Above-average frequency of forward-looking modal verbs ("expect", "anticipate", "see").',
  },
  kpi: {
    confidence: 86,
    redFlags: 3,
    entities: 28,
    sections: 11,
  },
  bullSignals: [
    {
      text: 'Guidance raised on revenue and services gross margin; buyback authorisation increased $35B.',
      paragraphRef: 'P14',
    },
    {
      text: 'iPhone +9% YoY ex-FX with channel inventory below the lower end of the target range — a setup for next quarter.',
      paragraphRef: 'P21',
    },
    {
      text: 'Services margin expansion of 280bps, with management explicitly attributing it to mix rather than one-time items.',
      paragraphRef: 'P34',
    },
  ],
  bearSignals: [
    {
      text: 'Greater China revenue down 7% YoY with management hedging on the timing of the rebound.',
      paragraphRef: 'P28',
    },
    {
      text: 'Three non-answers in Q&A on competitive AI positioning vs. on-device LLM peers.',
      paragraphRef: 'Q4, Q9, Q12',
    },
    {
      text: 'Capex envelope unchanged despite management language pointing to accelerating AI infrastructure spend.',
      paragraphRef: 'P41',
    },
  ],
  dimensions: [
    {
      label: 'Tone & Confidence',
      value: 0.82,
      qualifier: 'HIGH',
      evidence: [
        { quote: 'We are confident in our trajectory through the second half of the fiscal year.', ref: 'P14' },
        { quote: 'We expect services growth to remain in the double digits.', ref: 'P34' },
      ],
    },
    {
      label: 'Forward-Looking Posture',
      value: 0.78,
      qualifier: 'HIGH',
      evidence: [
        { quote: 'We are raising our outlook for the September quarter.', ref: 'P14' },
      ],
    },
    {
      label: 'Hedging & Qualifiers',
      value: 0.32,
      qualifier: 'MODERATE',
      evidence: [
        { quote: 'We continue to monitor the macro environment in Greater China.', ref: 'P28' },
      ],
    },
    {
      label: 'Q&A Discipline',
      value: 0.71,
      qualifier: 'ELEVATED',
    },
    {
      label: 'Quantification',
      value: 0.66,
      qualifier: 'ELEVATED',
    },
  ],
  radar: [
    { label: 'Tone', value: 0.82 },
    { label: 'Forward-Looking', value: 0.78 },
    { label: 'Directness', value: 0.68 },
    { label: 'Q&A Discipline', value: 0.71 },
    { label: 'Quantification', value: 0.66 },
  ],
  entities: [
    { name: 'Tim Cook', type: 'Person', verdict: 'BULLISH', score: 0.6, context: 'Repeatedly emphasised confidence in the second-half trajectory and AI rollout cadence.' },
    { name: 'Luca Maestri', type: 'Person', verdict: 'BULLISH', score: 0.5, context: 'Raised guidance with quantified bridge from FX, services mix, and gross margin.' },
    { name: 'Services', type: 'Segment', verdict: 'BULLISH', score: 0.84, context: '+14% YoY with 280bps margin expansion attributed to mix.' },
    { name: 'iPhone', type: 'Product', verdict: 'BULLISH', score: 0.62, context: '+9% YoY ex-FX; channel inventory lean entering Q3.' },
    { name: 'Vision Pro', type: 'Product', verdict: 'BULLISH', score: 0.45, context: 'Enterprise pipeline cited; consumer ramp slower than expected but improving.' },
    { name: 'Greater China', type: 'Geography', verdict: 'BEARISH', score: -0.42, context: 'Revenue -7% YoY; management framed as timing, but qualifiers stacked.' },
    { name: 'Mac', type: 'Product', verdict: 'MIXED', score: 0.05, context: 'Flat YoY; management cited tough compare with M3 launch quarter.' },
    { name: 'Apple Intelligence', type: 'Product', verdict: 'BULLISH', score: 0.55, context: 'Feature rollout on schedule; engagement metrics ahead of internal plan.' },
  ],
  sections: [
    { idx: 1, title: 'Safe Harbor', tags: ['boilerplate'], summary: 'Standard forward-looking statement disclaimer.', range: 'P1–P3', verdict: 'MIXED', score: 0.0 },
    { idx: 2, title: 'Opening Remarks — Tim Cook', tags: ['strategy', 'highlights'], summary: 'Confident framing of beat across iPhone, services, and capital return.', range: 'P4–P12', verdict: 'BULLISH', score: 0.74 },
    { idx: 3, title: 'Capital Return & Guidance', tags: ['guidance', 'capital'], summary: 'Buyback raised; revenue and gross margin outlook lifted.', range: 'P13–P18', verdict: 'BULLISH', score: 0.86 },
    { idx: 4, title: 'iPhone Performance', tags: ['product', 'iPhone'], summary: 'Lean channel; positive ASP mix; iPhone 17 demand cited as the strongest in three cycles.', range: 'P19–P24', verdict: 'BULLISH', score: 0.71 },
    { idx: 5, title: 'Greater China', tags: ['geography', 'risk'], summary: 'Down 7% with hedged language; management points to timing recovery.', range: 'P25–P30', verdict: 'BEARISH', score: -0.38 },
    { idx: 6, title: 'Services Deep Dive', tags: ['services', 'margin'], summary: 'Margin expansion attributed to mix; subscriber count ahead of plan.', range: 'P31–P37', verdict: 'BULLISH', score: 0.84 },
    { idx: 7, title: 'AI & Apple Intelligence', tags: ['ai', 'product'], summary: 'Rollout on schedule; some non-answers on on-device peer comparisons.', range: 'P38–P44', verdict: 'MIXED', score: 0.18 },
    { idx: 8, title: 'Q&A — Margins & Mix', tags: ['q&a', 'margin'], summary: 'Direct, quantified answers on services margin durability.', range: 'Q1–Q4', verdict: 'BULLISH', score: 0.62 },
    { idx: 9, title: 'Q&A — China & Macro', tags: ['q&a', 'risk'], summary: 'Hedged but transparent; quantified the timing assumptions.', range: 'Q5–Q8', verdict: 'MIXED', score: 0.0 },
    { idx: 10, title: 'Q&A — AI Competitive', tags: ['q&a', 'ai'], summary: 'Three non-answers; deflection to product roadmap.', range: 'Q9–Q12', verdict: 'BEARISH', score: -0.21 },
    { idx: 11, title: 'Closing Remarks', tags: ['summary'], summary: 'Reiterated confidence and capital return.', range: 'P45–P48', verdict: 'BULLISH', score: 0.65 },
  ],
  qna: [
    {
      id: 'q1',
      analyst: 'Erik Woodring', firm: 'Morgan Stanley', analystKind: 'sell-side',
      respondedBy: 'Luca Maestri',
      question: 'Walk us through the services margin trajectory through year-end.',
      answerSummary: 'Maestri quantified mix-driven gains and attributed 60% of expansion to advertising and storage.',
      insight: 'Direct, quantified, no hedging.', verdict: 'BULLISH', flags: ['Confident'],
    },
    {
      id: 'q2',
      analyst: 'Wamsi Mohan', firm: 'Bank of America', analystKind: 'sell-side',
      respondedBy: 'Tim Cook',
      question: 'How should we think about iPhone units into the holiday quarter?',
      answerSummary: 'Cook flagged lean channel and "the strongest demand setup we have seen in three cycles".',
      insight: 'Strong forward-looking signal with a quantified comparison.',
      verdict: 'BULLISH', flags: ['Confident'],
    },
    {
      id: 'q3',
      analyst: 'Aaron Rakers', firm: 'Wells Fargo', analystKind: 'sell-side',
      respondedBy: 'Tim Cook',
      question: 'On-device AI competitive positioning?',
      answerSummary: 'Cook deflected to roadmap; did not answer the specific peer comparison asked.',
      insight: 'Classic deflection — pivots to product narrative.',
      verdict: 'BEARISH', flags: ['Non-Answer', 'Deflection'],
      followUps: [
        {
          question: 'Specifically on benchmark performance vs. on-device peers — can you put numbers on that?',
          answerSummary: 'Cook reiterated the prior framing about quality and privacy; no benchmarks shared.',
          pressLevel: 'firm',
        },
        {
          question: 'When will we see independent third-party benchmarks?',
          answerSummary: 'Cook said "in due course" without committing to a window.',
          pressLevel: 'sharp',
        },
      ],
    },
    {
      id: 'q4',
      analyst: 'Krish Sankar', firm: 'TD Cowen', analystKind: 'sell-side',
      respondedBy: 'Luca Maestri',
      question: 'Greater China — at what point does timing become structural?',
      answerSummary: 'Maestri quantified the comp dynamic and reiterated 1H FY27 inflection assumption.',
      insight: 'Hedged but transparent — quantified the assumption.',
      verdict: 'MIXED', flags: ['Hedged'],
      followUps: [
        {
          question: 'And if 1H FY27 inflection slips, what is the contingency on capital return?',
          answerSummary: 'Maestri said capital return commitment is independent of segment timing.',
          pressLevel: 'mild',
        },
      ],
    },
    {
      id: 'q5',
      analyst: 'Sidney Ho', firm: 'Deutsche Bank', analystKind: 'sell-side',
      respondedBy: 'Luca Maestri',
      question: 'Capex envelope given AI infra build-out?',
      answerSummary: 'Maestri reaffirmed the existing envelope and cited cloud partner offload.',
      insight: 'Direct on the question, but the answer raises follow-up risk.',
      verdict: 'MIXED', flags: ['Confident'],
    },
    {
      id: 'q6',
      analyst: 'David Vogt', firm: 'UBS', analystKind: 'sell-side',
      respondedBy: 'Tim Cook',
      question: 'How durable is services growth into FY27?',
      answerSummary: 'Cook cited subscriber growth, ARPU expansion, and product attach rate.',
      insight: 'Strong, layered answer.',
      verdict: 'BULLISH', flags: ['Confident'],
    },
  ],
  numerical: [
    {
      metric: 'Total Revenue', value: '$94.4B', numericValue: 94.4, period: 'Q2 FY26', direction: 'up',
      framing: 'Beat consensus by $1.2B; raised outlook implies $3B beat trajectory.', aligned: true,
      consensus: { value: '$93.2B', numericValue: 93.2, source: 'Visible Alpha (28 brokers)' }, beat: 'beat',
    },
    {
      metric: 'iPhone Revenue', value: '$45.9B', numericValue: 45.9, period: 'Q2 FY26', direction: 'up',
      framing: '+9% YoY ex-FX; channel inventory below low end of band.', aligned: true,
      consensus: { value: '$44.6B', numericValue: 44.6, source: 'Visible Alpha' }, beat: 'beat',
    },
    {
      metric: 'Services Revenue', value: '$24.2B', numericValue: 24.2, period: 'Q2 FY26', direction: 'up',
      framing: '+14% YoY; margin +280bps from mix.', aligned: true,
      consensus: { value: '$23.4B', numericValue: 23.4, source: 'Visible Alpha' }, beat: 'beat',
    },
    {
      metric: 'Greater China Revenue', value: '$14.7B', numericValue: 14.7, period: 'Q2 FY26', direction: 'down',
      framing: 'Down 7% YoY; framed as timing-driven.', aligned: true,
      consensus: { value: '$15.6B', numericValue: 15.6, source: 'Visible Alpha' }, beat: 'miss',
    },
    {
      metric: 'Gross Margin', value: '46.8%', numericValue: 46.8, period: 'Q2 FY26', direction: 'up',
      framing: 'Up 110bps QoQ; services mix attribution.', aligned: true,
      consensus: { value: '46.4%', numericValue: 46.4, source: 'Visible Alpha' }, beat: 'beat',
    },
    {
      metric: 'Buyback Authorization', value: '$135B', numericValue: 135, period: 'New', direction: 'up',
      framing: 'Raised by $35B; signals capital-return confidence.', aligned: true,
    },
    {
      metric: 'Capex', value: '$3.1B', numericValue: 3.1, period: 'Q2 FY26', direction: 'star',
      framing: 'Unchanged envelope; flagged for AI infra follow-up.', aligned: true,
    },
    {
      metric: 'EPS (non-GAAP)', value: '$1.62', numericValue: 1.62, period: 'Q2 FY26', direction: 'up',
      framing: 'Beat by $0.05; buyback adds $0.02 to forward run-rate.', aligned: true,
      consensus: { value: '$1.57', numericValue: 1.57, source: 'Visible Alpha' }, beat: 'beat',
      nonGaap: true,
    },
    {
      metric: 'EPS (GAAP)', value: '$1.55', numericValue: 1.55, period: 'Q2 FY26', direction: 'up',
      framing: 'Stock-based comp + restructuring create $0.07 non-GAAP wedge.', aligned: true,
    },
  ],
  linguisticIndicators: [
    { label: 'Hedge Density', value: '0.18', rawValue: 0.18, tooltip: '% of clauses with hedging modal verbs. Lower than 4-quarter average (0.24).' },
    { label: 'Forward Modals', value: '0.41', rawValue: 0.82, tooltip: 'Frequency of forward-looking verbs ("expect", "anticipate", "see").' },
    { label: 'Active Voice', value: '0.93', rawValue: 0.93, tooltip: 'Active vs passive ratio. Above brand-typical average.' },
    { label: 'Sentence Length', value: '21 words', rawValue: 0.55, tooltip: 'Median sentence length. Calibrated for cadence.' },
    { label: 'Lexical Density', value: '0.62', rawValue: 0.62, tooltip: 'Content words / total words.' },
    { label: 'Modal Confidence', value: '0.74', rawValue: 0.74, tooltip: '"will / shall / are" frequency vs "may / might / could".' },
    { label: 'Quantification', value: '41 claims', rawValue: 0.66, tooltip: 'Discrete numerical claims in prepared remarks + Q&A.' },
    { label: 'Negation', value: '0.07', rawValue: 0.14, tooltip: '% of clauses with explicit negation. Low — confident posture.' },
  ],
  fingerprint: [
    { label: 'Confidence Words', value: 0.84 },
    { label: 'Forward-Looking Verbs', value: 0.76 },
    { label: 'Quantified Claims', value: 0.66 },
    { label: 'Active Voice', value: 0.93 },
    { label: 'Concrete Nouns', value: 0.71 },
    { label: 'Hedges (inv.)', value: 0.68 },
    { label: 'Filler Phrases (inv.)', value: 0.81 },
    { label: 'Negation (inv.)', value: 0.86 },
  ],
  sectorLexicon: [
    {
      name: 'Consumer Hardware',
      matches: 64,
      pos: 41,
      neg: 9,
      risk: 14,
      score: 0.52,
      terms: {
        positive: ['installed base', 'channel inventory', 'units', 'ASP', 'demand'],
        negative: ['promotional', 'discount', 'destocking'],
        risk: ['supply', 'tariff', 'component cost'],
      },
    },
    {
      name: 'Software & Services',
      matches: 52,
      pos: 38,
      neg: 4,
      risk: 10,
      score: 0.76,
      terms: {
        positive: ['ARPU', 'attach rate', 'subscribers', 'recurring', 'mix'],
        negative: ['churn'],
        risk: ['regulatory', 'app store'],
      },
    },
    {
      name: 'Capital Return',
      matches: 18,
      pos: 16,
      neg: 0,
      risk: 2,
      score: 0.88,
      terms: {
        positive: ['buyback', 'authorization', 'dividend', 'return'],
        risk: ['payout ratio'],
      },
    },
    {
      name: 'AI / Compute',
      matches: 23,
      pos: 14,
      neg: 4,
      risk: 5,
      score: 0.31,
      terms: {
        positive: ['Apple Intelligence', 'on-device', 'rollout', 'engagement'],
        negative: ['behind', 'catch up'],
        risk: ['capex', 'compute', 'partner cloud'],
      },
    },
  ],
  riskQuotes: [
    { quote: 'We continue to monitor the macro environment in Greater China.', ref: 'P28', category: 'demand' },
    { quote: 'We will revisit our infrastructure envelope as we see ramp in workloads.', ref: 'P41', category: 'cost' },
    { quote: 'We are evaluating our position relative to recent regulatory developments in the EU.', ref: 'P43', category: 'litigation' },
  ],
  verification: [
    { label: 'Numerical claims aligned with framing', status: 'pass', note: '41 of 41 numerical claims match their qualitative framing.' },
    { label: 'Forward-looking statements properly flagged', status: 'pass' },
    { label: 'Q&A non-answers detected', status: 'flag', note: '3 detected on AI competitive positioning. Logged in Q&A Deep Dive.' },
    { label: 'Sector lexicon coverage', status: 'pass' },
    { label: 'Source paragraphs cite-aligned', status: 'pass' },
  ],
  advanced: [
    { key: 'Model Version', value: 'tia-sentiment-v3.4' },
    { key: 'Total Tokens', value: '24,118' },
    { key: 'Sections Detected', value: '11' },
    { key: 'Q&A Pairs', value: '14' },
    { key: 'Entities Detected', value: '28' },
    { key: 'Latency', value: '4.7s' },
    { key: 'Run ID', value: 'run_2qxr8nt7v3' },
  ],

  speakers: [
    {
      name: 'Tim Cook', role: 'CEO', score: 0.78, utterances: 42,
      hedgeDensity: 0.14, forwardModalDensity: 0.46, qualifier: 'HIGH',
      delta: 0.06, highlight: 'Confidence elevated vs Q1; one notable AI deflection.',
    },
    {
      name: 'Luca Maestri', role: 'CFO', score: 0.62, utterances: 28,
      hedgeDensity: 0.21, forwardModalDensity: 0.38, qualifier: 'ELEVATED',
      delta: 0.04, highlight: 'Quantified guidance bridge; calm on China timing.',
    },
    {
      name: 'Suhasini Chandramouli', role: 'IR', score: 0.0, utterances: 14,
      hedgeDensity: 0.05, forwardModalDensity: 0.04, qualifier: 'LOW',
      highlight: 'Operator role; neutral utterances only.',
    },
  ],

  cohort: {
    peerSet: 'Consumer Hardware · Q2 2026',
    peerCount: 38,
    windowLabel: 'Last 30 days',
    overallPercentile: 87,
    stats: [
      { label: 'Tone & Confidence', value: 0.82, cohortMean: 0.58, cohortP10: 0.32, cohortP90: 0.84, percentile: 89 },
      { label: 'Forward-Looking Posture', value: 0.78, cohortMean: 0.54, cohortP10: 0.28, cohortP90: 0.81, percentile: 84 },
      { label: 'Directness', value: 0.68, cohortMean: 0.52, cohortP10: 0.24, cohortP90: 0.76, percentile: 78 },
      { label: 'Q&A Discipline', value: 0.71, cohortMean: 0.61, cohortP10: 0.36, cohortP90: 0.83, percentile: 72 },
      { label: 'Quantification', value: 0.66, cohortMean: 0.55, cohortP10: 0.29, cohortP90: 0.78, percentile: 68 },
    ],
  },

  priorQuarter: {
    slug: 'apple-q1-fy26',
    title: 'Apple Q1 FY26 Earnings Call',
    date: '2026-01-30T22:00:00Z',
    verdict: 'BULLISH',
    score: 0.58,
    confidence: 81,
    dimensionScores: {
      'Tone & Confidence': 0.74,
      'Forward-Looking Posture': 0.68,
      'Hedging & Qualifiers': 0.41,
      'Q&A Discipline': 0.65,
      'Quantification': 0.62,
    },
    speakerScores: { 'Tim Cook': 0.72, 'Luca Maestri': 0.58 },
  },

  tailRisk: {
    overallScore: 0.34,
    weightedExposure: -0.12, // mildly downside-skewed
    qualifier: 'MODERATE',
    terms: [
      { term: 'could', count: 11, impliedProbability: 0.42, context: 'guidance' },
      { term: 'may', count: 9, impliedProbability: 0.38, context: 'macro' },
      { term: 'might', count: 4, impliedProbability: 0.28, context: 'competitive' },
      { term: 'possibly', count: 2, impliedProbability: 0.22, context: 'litigation' },
      { term: 'we expect', count: 18, impliedProbability: 0.78, context: 'guidance' },
    ],
    topQuotes: [
      { quote: 'We could see continued strength in services through year-end.', ref: 'P34', term: 'could' },
      { quote: 'Macro conditions in Greater China may take an additional quarter to normalize.', ref: 'P28', term: 'may' },
      { quote: 'Regulatory developments in the EU could affect our business in the near term.', ref: 'P43', term: 'could' },
    ],
  },

  guidanceBridge: {
    netVerdict: 'beat',
    summary: 'Q3 implied bridge runs $1.6B above Street midpoint at the high end; services margin guidance is the single biggest delta.',
    items: [
      {
        metric: 'Q3 Revenue',
        rangeLo: 96.0, rangeHi: 98.0, unit: '$B',
        consensusMid: 95.4, consensusSource: 'Visible Alpha',
        delta: 1.6, framing: 'raise',
        note: 'Midpoint $97B vs consensus $95.4B → +$1.6B implied beat.',
      },
      {
        metric: 'Q3 Services Gross Margin',
        rangeLo: 73.5, rangeHi: 74.5, unit: '%',
        consensusMid: 72.8, consensusSource: 'Visible Alpha',
        delta: 1.2, framing: 'raise',
      },
      {
        metric: 'Q3 Operating Margin',
        rangeLo: 30.5, rangeHi: 31.5, unit: '%',
        consensusMid: 30.4, consensusSource: 'Visible Alpha',
        delta: 0.6, framing: 'reaffirm',
      },
      {
        metric: 'FY26 Capex',
        rangeLo: 12.5, rangeHi: 13.5, unit: '$B',
        consensusMid: 13.8, consensusSource: 'Visible Alpha',
        delta: -0.8, framing: 'reaffirm',
        note: 'Below consensus — flagged as follow-up risk if AI infra spend accelerates.',
      },
    ],
  },
};
